import { useEffect, useState } from "react";
import BackButton from "@/components/BackButton";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Trophy,
  PlayCircle,
  CheckCircle2,
  Loader2,
  ShieldAlert,
  Edit2,
  Coins,
  XCircle,
  Clock,
  ExternalLink,
  Link2,
  Copy,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Admin() {
  const { user: currentUser } = useAuth();
  const [publicBaseUrl, setPublicBaseUrl] = useState("");
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [draws, setDraws] = useState([]);
  const [prize, setPrize] = useState("Nedeljna nagrada");
  const [drawing, setDrawing] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const [editing, setEditing] = useState(null); // referral id
  const [editForm, setEditForm] = useState({ status: "neaktivan", investment_amount: 0 });
  const [cryptoPurchases, setCryptoPurchases] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [cryptoFilter, setCryptoFilter] = useState("pending"); // pending | approved | rejected | all
  const [actingId, setActingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const [u, r, d, c, pc] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/referrals"),
      api.get("/admin/lottery/draws"),
      api.get(`/admin/crypto-purchases${cryptoFilter === "all" ? "" : `?status=${cryptoFilter}`}`),
      api.get("/config/public"),
    ]);
    setUsers(u.data.users);
    setReferrals(r.data.referrals);
    setDraws(d.data.draws);
    setCryptoPurchases(c.data.purchases);
    setPendingCount(c.data.pending_count);
    setPublicBaseUrl((pc.data.public_base_url || "").replace(/\/+$/, ""));
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoFilter]);

  const approveCrypto = async (id) => {
    setActingId(id);
    try {
      const { data } = await api.post(`/admin/crypto-purchases/${id}/approve`);
      const credit = data.credit || {};
      if (credit.cycle_closed) {
        toast.success(`Odobreno · CIKLUS ZATVOREN! Pobednik: ${credit.draw?.winner_name || "?"}`);
      } else {
        toast.success(`Odobreno · +${credit.tokens_awarded || 0} tokena dodeljeno`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActingId(null);
    }
  };

  const rejectCrypto = async (id) => {
    setActingId(id);
    try {
      await api.post(`/admin/crypto-purchases/${id}/reject`, { reason: rejectReason || null });
      toast.success("Transakcija odbijena");
      setRejectingId(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActingId(null);
    }
  };

  const drawLottery = async () => {
    setDrawing(true);
    setLastWinner(null);
    try {
      const { data } = await api.post("/admin/lottery/draw", { prize });
      setLastWinner(data);
      toast.success(`Pobednik: ${data.winner_name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setEditForm({ status: r.status, investment_amount: r.investment_amount });
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/admin/referrals/${id}`, {
        status: editForm.status,
        investment_amount: parseFloat(editForm.investment_amount) || 0,
      });
      toast.success("Sačuvano");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalTickets = users.filter((u) => u.role !== "admin").reduce((s, u) => s + (u.tickets || 0), 0);

  // Build admin referral link. Prefer the configured PUBLIC_BASE_URL so the link
  // points at the production domain even when the admin is logged into a preview.
  // Falls back to the current origin if the env is empty.
  const refCode = currentUser?.referral_code || "";
  const refBase = publicBaseUrl || (typeof window !== "undefined" ? window.location.origin : "");
  const refLink = refCode ? `${refBase}/?ref=${refCode}` : "";

  const copyRefLink = async () => {
    if (!refLink) return;
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success("Referal link kopiran");
    } catch {
      toast.error("Kopiranje nije uspelo");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="admin-page">
      <div className="flex justify-end mb-4"><BackButton /></div>
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-[#ff7a18]" />
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Administracija</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-wide mt-1">
            Admin <span className="text-neon-orange">panel</span>
          </h1>
        </div>
      </div>

      {/* Admin referral link */}
      {refLink && (
        <div
          data-testid="admin-referral-card"
          className="nasa-card p-5 md:p-6 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(34,255,119,0.06), rgba(255,122,24,0.04)), #0a0a0a",
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#22ff77]/15 border border-[#22ff77]/40 flex items-center justify-center text-[#22ff77] shrink-0">
              <Link2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Moj referal link</div>
              <h2 className="font-display text-lg font-bold mt-0.5">
                Kod: <span className="text-[#22ff77]" data-testid="admin-referral-code">{refCode}</span>
              </h2>
              <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                Svaka prijava preko ovog linka biće automatski povezana sa tvojim adminskim nalogom kao referal.
              </p>
              <div className="mt-3 flex flex-col md:flex-row gap-2">
                <div
                  data-testid="admin-referral-link"
                  className="flex-1 min-w-0 font-mono text-[12px] md:text-sm break-all bg-black/60 border border-white/10 px-3 py-3 rounded-sm text-[#22ff77]"
                >
                  {refLink}
                </div>
                <button
                  onClick={copyRefLink}
                  data-testid="copy-admin-referral-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#22ff77] text-black font-bold text-[11px] uppercase tracking-[0.25em] rounded-sm hover:brightness-110 transition glow-green"
                >
                  <Copy className="w-4 h-4" />
                  Kopiraj link
                </button>
              </div>
              {!publicBaseUrl && (
                <p className="text-[10px] text-[#ff7a18] mt-2">
                  ⚠ Link koristi trenutni domen. Za fiksni produkcioni link, postavi <code className="font-mono">PUBLIC_BASE_URL</code> u backend .env (npr. <code className="font-mono">https://www.nashaadria.com</code>).
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Korisnika" value={users.length} color="#22ff77" />
        <Stat label="Referala" value={referrals.length} color="#22ff77" />
        <Stat label="Tokeni u igri" value={totalTickets} color="#ff7a18" />
        <Stat label="USDT na čekanju" value={pendingCount} color="#ff7a18" />
      </div>

      {/* Crypto USDT purchases */}
      <div className="nasa-card overflow-hidden" data-testid="admin-crypto-purchases">
        <div className="p-6 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#22ff77]" />
            <h2 className="font-display text-lg">USDT (TRC20) uplate · ručno odobravanje</h2>
            {pendingCount > 0 && (
              <span data-testid="pending-badge" className="ml-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-[#ff7a18] text-black font-bold">
                {pendingCount} na čekanju
              </span>
            )}
          </div>
          <div className="flex gap-2 text-[10px] uppercase tracking-widest">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button
                key={f}
                data-testid={`crypto-filter-${f}`}
                onClick={() => setCryptoFilter(f)}
                className={`px-3 py-1.5 rounded-sm border ${cryptoFilter === f ? "bg-[#22ff77] text-black border-[#22ff77]" : "border-white/10 text-zinc-400 hover:border-[#22ff77]/40"}`}
              >
                {f === "all" ? "sve" : f === "pending" ? "na čekanju" : f === "approved" ? "odobreno" : "odbijeno"}
              </button>
            ))}
          </div>
        </div>

        {cryptoPurchases.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">Nema uplata u ovom filteru.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                  <th className="text-left px-4 py-3">Datum</th>
                  <th className="text-left px-4 py-3">Korisnik</th>
                  <th className="text-left px-4 py-3">Metod</th>
                  <th className="text-right px-4 py-3">EUR</th>
                  <th className="text-right px-4 py-3">USDT</th>
                  <th className="text-right px-4 py-3">Tiketi</th>
                  <th className="text-left px-4 py-3">TxID</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {cryptoPurchases.map((cp) => {
                  const isPending = cp.status === "pending";
                  const isRejecting = rejectingId === cp.id;
                  const tronUrl = `https://tronscan.org/#/transaction/${encodeURIComponent(cp.tx_id || "")}`;
                  return (
                    <tr key={cp.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`crypto-row-${cp.id}`}>
                      <td className="px-4 py-3 text-zinc-500 text-[11px] font-mono whitespace-nowrap">
                        {new Date(cp.created_at).toLocaleString("sr-Latn-RS")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{cp.user_name || "—"}</div>
                        <div className="text-[10px] text-zinc-500">{cp.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs uppercase tracking-widest text-zinc-300">
                        {cp.payment_method === "card" ? "Kartica" : "Kripto"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[#ff7a18]">{cp.amount_eur.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right font-mono text-[#22ff77]">{cp.amount_usdt}</td>
                      <td className="px-4 py-3 text-right">{cp.quantity}</td>
                      <td className="px-4 py-3">
                        <a
                          href={tronUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-mono text-[#22ff77] hover:underline max-w-[160px] truncate"
                          title={cp.tx_id}
                        >
                          {(cp.tx_id || "").slice(0, 14)}…
                          <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                        {cp.note && (
                          <div className="text-[10px] text-zinc-500 mt-1 max-w-[160px] truncate" title={cp.note}>
                            {cp.note}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={cp.status} />
                        {cp.status === "rejected" && cp.rejected_reason && (
                          <div className="text-[10px] text-zinc-500 mt-1 max-w-[150px]">razlog: {cp.rejected_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isPending && !isRejecting && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => approveCrypto(cp.id)}
                              disabled={actingId === cp.id}
                              data-testid={`approve-${cp.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#22ff77] text-black text-[10px] font-bold rounded-sm uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
                            >
                              {actingId === cp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              Odobri
                            </button>
                            <button
                              onClick={() => { setRejectingId(cp.id); setRejectReason(""); }}
                              data-testid={`reject-${cp.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-500/50 text-red-400 text-[10px] font-bold rounded-sm uppercase tracking-widest hover:bg-red-500/10"
                            >
                              <XCircle className="w-3 h-3" /> Odbij
                            </button>
                          </div>
                        )}
                        {isPending && isRejecting && (
                          <div className="flex justify-end gap-2 items-center">
                            <input
                              data-testid={`reject-reason-${cp.id}`}
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="razlog (opciono)"
                              className="w-32 bg-black border border-white/10 px-2 py-1 text-[11px] rounded-sm"
                            />
                            <button
                              onClick={() => rejectCrypto(cp.id)}
                              disabled={actingId === cp.id}
                              className="px-2 py-1 bg-red-500 text-white text-[10px] font-bold rounded-sm uppercase"
                            >
                              Potvrdi
                            </button>
                            <button
                              onClick={() => { setRejectingId(null); setRejectReason(""); }}
                              className="px-2 py-1 border border-white/10 text-[10px] rounded-sm"
                            >
                              X
                            </button>
                          </div>
                        )}
                        {!isPending && (
                          <span className="text-[10px] text-zinc-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lottery draw */}
      <div className="nasa-card p-6 md:p-8" data-testid="lottery-draw-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">
          <Trophy className="w-3 h-3 text-[#ff7a18]" /> Izvlačenje nagrade
        </div>
        <h2 className="font-display text-2xl mb-4">
          Izvuci <span className="text-neon-orange">pobednika</span> NAŠA lutrije
        </h2>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            data-testid="prize-input"
            value={prize}
            onChange={(e) => setPrize(e.target.value)}
            placeholder="Opis nagrade (npr. 100 USDT, iPhone 16)"
            className="flex-1 bg-black border border-white/10 px-3 py-3 text-sm rounded-sm focus:outline-none focus:border-[#ff7a18]"
          />
          <button
            onClick={drawLottery}
            disabled={drawing || totalTickets === 0}
            data-testid="draw-button"
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#ff7a18] text-black font-bold uppercase tracking-widest text-sm rounded-sm hover:brightness-110 glow-orange disabled:opacity-50"
          >
            {drawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {drawing ? "Izvlačenje..." : "Izvuci pobednika"}
          </button>
        </div>

        {lastWinner && (
          <div data-testid="winner-banner" className="border border-[#22ff77]/40 bg-[#22ff77]/5 p-4 rounded-sm">
            <div className="flex items-center gap-2 text-[#22ff77] mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-display font-bold uppercase tracking-widest text-xs">Pobednik izvučen!</span>
            </div>
            <div className="font-display text-2xl">{lastWinner.winner_name}</div>
            <div className="text-xs text-zinc-400 mt-1">
              {lastWinner.winner_email} · {lastWinner.winner_tickets} tokena od ukupno {lastWinner.total_tickets_in_pool}
            </div>
            <div className="text-[#ff7a18] font-display text-sm mt-2">Nagrada: {lastWinner.prize}</div>
          </div>
        )}

        {draws.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Prethodna izvlačenja</div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {draws.map((d) => (
                <div key={d.id} className="flex justify-between items-center border-b border-white/5 py-2 text-sm">
                  <div>
                    <div className="font-medium">{d.winner_name}</div>
                    <div className="text-xs text-zinc-500">{d.prize}</div>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {new Date(d.drawn_at).toLocaleString("sr-Latn-RS")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Referrals management */}
      <div className="nasa-card overflow-hidden" data-testid="admin-referrals">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#22ff77]" />
            <h2 className="font-display text-lg">Potvrda članstava</h2>
          </div>
          <div className="text-xs text-zinc-500">{referrals.length} ukupno</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                <th className="text-left px-6 py-3">Prijatelj</th>
                <th className="text-left px-6 py-3">Email</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Ulog (€)</th>
                <th className="text-right px-6 py-3">Akcija</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => {
                const isEdit = editing === r.id;
                return (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 py-4">{r.name}</td>
                    <td className="px-6 py-4 text-zinc-400 text-xs">{r.email}</td>
                    <td className="px-6 py-4">
                      {isEdit ? (
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                          className="bg-black border border-white/10 px-2 py-1 text-xs rounded-sm"
                        >
                          <option value="aktivan">aktivan</option>
                          <option value="neaktivan">neaktivan</option>
                        </select>
                      ) : (
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                          r.status === "aktivan"
                            ? "text-[#22ff77] border-[#22ff77]/40 bg-[#22ff77]/5"
                            : "text-[#ff7a18] border-[#ff7a18]/40 bg-[#ff7a18]/5"
                        }`}>
                          {r.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      {isEdit ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.investment_amount}
                          onChange={(e) => setEditForm((f) => ({ ...f, investment_amount: e.target.value }))}
                          className="w-28 bg-black border border-white/10 px-2 py-1 text-xs text-right rounded-sm"
                        />
                      ) : (
                        `€${r.investment_amount.toFixed(2)}`
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isEdit ? (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => saveEdit(r.id)} className="px-3 py-1 bg-[#22ff77] text-black text-xs font-bold rounded-sm uppercase">Sačuvaj</button>
                          <button onClick={() => setEditing(null)} className="px-3 py-1 border border-white/10 text-xs rounded-sm">X</button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(r)} className="inline-flex items-center gap-1 text-xs text-[#22ff77] hover:underline">
                          <Edit2 className="w-3 h-3" /> Izmeni
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users */}
      <div className="nasa-card overflow-hidden" data-testid="admin-users">
        <div className="p-6 border-b border-white/5 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#22ff77]" />
          <h2 className="font-display text-lg">Svi korisnici</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                <th className="text-left px-6 py-3">Ime</th>
                <th className="text-left px-6 py-3">Email</th>
                <th className="text-left px-6 py-3">Država</th>
                <th className="text-left px-6 py-3">Kod</th>
                <th className="text-right px-6 py-3">Tokeni</th>
                <th className="text-right px-6 py-3">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5">
                  <td className="px-6 py-4">{u.name} {u.role === "admin" && <span className="ml-2 text-[10px] text-[#ff7a18] uppercase">admin</span>}</td>
                  <td className="px-6 py-4 text-zinc-400 text-xs">{u.email}</td>
                  <td className="px-6 py-4 text-zinc-400 text-xs">{u.country || "—"}</td>
                  <td className="px-6 py-4 font-mono text-xs text-[#22ff77]">{u.referral_code}</td>
                  <td className="px-6 py-4 text-right font-display font-bold text-[#22ff77]">{u.tickets}</td>
                  <td className="px-6 py-4 text-right font-mono">{(u.balance ?? 0).toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-white/10 bg-black/40 p-4 rounded-sm">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "#ff7a18", label: "Na čekanju", Icon: Clock },
    approved: { c: "#22ff77", label: "Odobreno", Icon: CheckCircle2 },
    rejected: { c: "#ef4444", label: "Odbijeno", Icon: XCircle },
  };
  const { c, label, Icon } = map[status] || { c: "#888", label: status, Icon: Clock };
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border"
      style={{ color: c, borderColor: `${c}66`, background: `${c}10` }}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}
